import React, { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react';
import { Autocomplete, Avatar, Box, Button, CircularProgress, Container, Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Employee } from "../../../types/Employee";
import { UmrahPaperRequest } from "../../../types/UmrahPaperRequest";
import { getDocs, collection, where, limit, orderBy, getFirestore, query, doc, getDoc, setDoc } from 'firebase/firestore';
import { useDebouncedCallback } from 'use-debounce';
import employeeConverter from '../../../converters/employeeConverter';
import { DateTime } from 'luxon';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'notistack';

type Props = {
  employee?: Employee;
  umrahPaperRequest?: UmrahPaperRequest;
  onSaved?: (umrahPaperRequest: UmrahPaperRequest) => void;
  onClosed?: () => void;
}
export const UmrahPaperRequestForm = (props: Props) => {
  const { umrahPaperRequest, onSaved, onClosed } = props;
  const editMode = umrahPaperRequest !== undefined;
  
  const { enqueueSnackbar } = useSnackbar();
  const db = getFirestore();

  const [employee, setEmployee] = useState<Employee | undefined>(props.employee);
  const [date, setDate] = useState(editMode ? umrahPaperRequest.date : DateTime.now().toFormat('yyyyMMdd'));
 
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [saving, setSaving] = useState(false);

  const searchDebounced = useDebouncedCallback((value: string) => {
    setLoadingEmployees(true);
    setEmployees([]);
    getDocs(
      query(collection(db, 'Employees'), 
      where('idNumber', '>=', value), 
      where('idNumber', '<=', value + '\uf8ff'),
      where('status', '==', 'Active'),
      limit(15),
      orderBy('idNumber'))
    .withConverter(employeeConverter)).then((qSnapshot) => {
      setEmployees(qSnapshot.docs.map((d) => d.data()));
      setLoadingEmployees(false);
    });
  }, 1000);

  const handleOpenAutocomplete = () => setOpenAutocomplete(true);
  const handleCloseAutocomplete = () => setOpenAutocomplete(false);

  const handleTexInputOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    searchDebounced(event.currentTarget.value);
  };

  const handleAutocompleteSelectChange = (_e: SyntheticEvent<Element, Event>, value: Employee | null) => {
    setEmployee(value ?? undefined);
  };

  const handleCancelOnClick = () => onClosed && onClosed();

  const handleSaveOnClick = () => {
    if (employee) {
      if (window.confirm((editMode ? 'Update ' : 'Create ') + ` employee ${employee.idNumber} umrah paper request?`)) {
        setSaving(true);

        let request: any = {
          id: uuidv4(),
          idNumber: employee.idNumber,
          name:  employee.name,
          date,
          dateRequested: DateTime.utc().toISO(),
          status: 'New',
          lastUpdate: DateTime.utc().toISO(),
        };

        if (editMode) {
          request = { id: umrahPaperRequest.id, date }
        }

        // Save
        setDoc(doc(db, `Employees/${employee.idNumber}/UmrahPaperRequests/${request.id}`), request, { merge: true })
          .then(() => {
            enqueueSnackbar(`Employee ${employee.idNumber} umrah paper request has been ` + (editMode ? 'updated!' : 'created!'), { variant: 'success' });

            setDate(DateTime.now().toFormat('yyyyMMdd'));
            setSaving(false);

            onSaved && onSaved(editMode ? { ...umrahPaperRequest, ...request } : request);
          })
          .catch((error) => {
            enqueueSnackbar(typeof(error) === 'string' ? error : error.message, { variant: 'success' });
          });
      }
    }
  };

  useEffect(() => {
    if (editMode) {
      // Get employee information
      const { idNumber } = umrahPaperRequest;
      if (props.employee === undefined) {
        getDoc(doc(db, `Employees/${idNumber}`).withConverter(employeeConverter)).then(docSnapshot => {
          setEmployee(docSnapshot.data());
        });
      }
    } else {
      if (props.employee) {
        setEmployee(employee);
      }
    }
  }, [editMode]);

  return (
    <Box>
      <Stack spacing={2.5} padding={3}>
        {employee !== undefined ? (
          <Box mb={2}>
            <Stack direction='row' spacing={2}>
              <Avatar src={employee.picture} />
              <Stack>
                <Typography fontWeight='700'>{employee.name}</Typography>
                <Typography variant='caption'>{employee.idNumber}</Typography>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Autocomplete
              id="asynchronous-demo"
              fullWidth
              open={openAutocomplete}
              onOpen={handleOpenAutocomplete}
              onClose={handleCloseAutocomplete}
              isOptionEqualToValue={(option, value) => option.idNumber === value.idNumber}
              getOptionLabel={option => `${option.idNumber} - ${option.name}`}
              options={employees}
              loading={loadingEmployees}
              noOptionsText="Enter employee id number..."
              onChange={handleAutocompleteSelectChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Id Number"
                  onChange={handleTexInputOnChanged}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}
        {employee && (
          <>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                label="Date"
                format="MMMM dd, yyyy"
                value={DateTime.fromFormat(date, 'yyyyMMdd')}
                onChange={(selectedDate: DateTime | null) => {
                  if (selectedDate !==  null) {
                    setDate(selectedDate.toFormat('yyyyMMdd'));
                  }
                }}
              />
            </LocalizationProvider>
          </>
        )}
      </Stack>
      <Divider />
      <Stack spacing={1} sx={{ p: 1 }} direction='row' justifyContent='right'>
        <Button color='error' onClick={handleCancelOnClick}>CANCEL</Button>
        <Button disabled={saving} onClick={handleSaveOnClick}>SAVE</Button>
      </Stack>
    </Box>
  );
};
